import { DownloadIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  FormControl,
  FormLabel,
  Image as ChakraImage,
  keyframes,
  Switch,
  Text,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { toPng } from "html-to-image";
import { atom, useAtom } from "jotai";
import { jsPDF } from "jspdf";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { InfoPopover } from "../components/InfoPopover";
import {
  CompareBaseTable,
  draggingColumnAtom,
} from "../compositions/compareTable/CompareBaseTable";
import { CompareEnergyTable } from "../compositions/compareTable/CompareEnergyTable";
import { CompareGeneralTable } from "../compositions/compareTable/CompareGeneralTable";
import { Page } from "../compositions/Page";
import { currentSituationAtom } from "../compositions/modals/SituationModal";
import {
  getResidenceType,
  getScenario,
  getSituation,
  ResidenceTypeModel,
  ScenarioModel,
} from "../clients/KompasApi";
import { ConfigureCompareViewModal } from "../compositions/modals/ConfigureCompareViewModal";
import { CompareEnergyMeasureTable } from "../compositions/compareTable/CompareEnergyMeasureTable";
import { CompareTotalPriceTable } from "../compositions/compareTable/CompareTotalPriceTable";
import { CompareTotalCO2ImpactTable } from "../compositions/compareTable/CompareTotalCO2ImpactTable";
import { ComparePageContextProvider } from "../shared/state/ComparePageContext";

export const onlyShowDifferencesAtom = atom(false);
export const highlightDifferencesAtom = atom(false);
export const pdfRecordAtom = atom(false);

const reorderKeyframes = keyframes`
0%, 100% {
  background-color: transparent;
}
50% {
  background-color: var(--chakra-colors-brand-red-10);
}
`;

const reorderAnimation = `${reorderKeyframes} 2s infinite`;

export function ComparePage() {
  const params = useParams<{
    residenceId: string;
    situationId: string;
    scenarioIds: string;
  }>();

  const [currentSituation, setCurrentSituation] = useAtom(currentSituationAtom);
  const [residenceType, setResidenceType] = useState<ResidenceTypeModel>();
  const [scenarios, setScenarios] = useState<ScenarioModel[]>([]);

  const residenceTypeId = useMemo(
    () => Number(params.residenceId ?? "0"),
    [params.residenceId],
  );

  const situationId = useMemo(
    () => Number(params.situationId ?? "0"),
    [params.situationId],
  );

  const selectedScenarioIds = useMemo(
    () => params.scenarioIds?.split("&").map(Number).filter(Boolean) ?? [],
    [params.scenarioIds],
  );

  const navigate = useNavigate();
  const [onlyShowDifferences, setOnlyShowDifferences] = useAtom(
    onlyShowDifferencesAtom,
  );
  const [highlightDifferences, setHighlightDifferences] = useAtom(
    highlightDifferencesAtom,
  );
  const [recordingPdf, setRecordingPdf] = useAtom(pdfRecordAtom);
  const [draggingColumn] = useAtom(draggingColumnAtom);

  useEffect(() => {
    if (!residenceTypeId) return;

    const abortController = new AbortController();

    getResidenceType({
      path: { id: residenceTypeId },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        setResidenceType(data);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        console.error("Error fetching residence type:", error);
      });

    return () =>
      abortController.abort("Component unmounted or residenceId changed");
  }, [residenceTypeId]);

  const residenceName = useMemo(
    () => residenceType?.name ?? "",
    [residenceType],
  );

  useEffect(() => {
    if (!residenceType || !situationId || currentSituation?.id === situationId)
      return;

    const abortController = new AbortController();

    getSituation({
      path: { residenceTypeId: residenceType.id, id: situationId },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        setCurrentSituation(data);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        console.error("Error fetching situation:", error);
      });
  }, [residenceType, situationId, currentSituation, setCurrentSituation]);

  useEffect(() => {
    if (!residenceType || !currentSituation || !selectedScenarioIds.length)
      return;

    const abortController = new AbortController();

    Promise.all(
      selectedScenarioIds.map((id) =>
        getScenario({
          path: {
            residenceTypeId: residenceType.id,
            situationId: currentSituation.id,
            id,
          },
          signal: abortController.signal,
          throwOnError: true,
        }),
      ),
    )
      .then((data) => {
        setScenarios(data.map((d) => d.data));
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        console.error("Error fetching scenarios:", error);
      });
  }, [residenceType, currentSituation, selectedScenarioIds]);

  const generateImage = useCallback(() => {
    setRecordingPdf(true);
    requestAnimationFrame(async () => {
      const node = document.getElementById("tables");
      const logo = document.getElementById("logo-header");
      const versionTag = document
        .getElementById("version-tag")
        ?.cloneNode(true);
      if (node && logo && versionTag instanceof HTMLElement) {
        const dataUrl = await toPng(node);
        const logoDataUrl = await toPng(logo);

        const exportTime = new Date().toLocaleString("nl-NL", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });

        versionTag.textContent += ` | ${exportTime}`;
        versionTag.style.margin = "0 8px 0 0";
        document.body.appendChild(versionTag);
        const versionTagDataUrl = await toPng(versionTag);
        document.body.removeChild(versionTag);

        const img = new Image();
        img.src = dataUrl;
        const logoImg = new Image();
        logoImg.src = logoDataUrl;
        const versionTagImg = new Image();
        versionTagImg.src = versionTagDataUrl;

        requestAnimationFrame(() => {
          const portraitFit =
            img.naturalHeight / img.naturalWidth > Math.sqrt(2) * 1.1;
          // eslint-disable-next-line new-cap
          const doc = new jsPDF(portraitFit ? "p" : "l", "mm", "a3");
          const height = doc.internal.pageSize.getHeight();
          const width = doc.internal.pageSize.getWidth();
          const scaleX = (height - 8) / img.height;
          const scaleY = (width - 8) / img.width;

          const scale = Math.min(scaleX, scaleY);

          doc.addImage(
            dataUrl,
            "PNG",
            4,
            4,
            img.width * scale,
            img.height * scale,
            undefined,
            "SLOW",
          );
          doc.addImage(
            "/logo.png",
            "PNG",
            8,
            8,
            logoImg.width * 0.1,
            logoImg.height * 0.1,
            undefined,
            "SLOW",
          );
          doc.addImage(
            versionTagDataUrl,
            "PNG",
            width - versionTagImg.width * 0.1 - 2,
            height - versionTagImg.height * 0.1 - 2,
            versionTagImg.width * 0.1,
            versionTagImg.height * 0.1,
            undefined,
            "SLOW",
          );
          doc.save(`Woningtype ${residenceName} - ${exportTime}.pdf`);
          requestAnimationFrame(() => {
            setRecordingPdf(false);
          });
        });
      }
    });
  }, [residenceName, setRecordingPdf]);

  const comparedOrientation = useMemo(
    () => currentSituation?.orientation.name,
    [currentSituation],
  );

  const comparedPosition = useMemo(
    () => currentSituation?.relativePosition.name,
    [currentSituation],
  );

  const comparisonParameters = useMemo(
    () =>
      [
        residenceName && `Woningtype ${residenceName}`,
        comparedOrientation,
        comparedPosition,
      ].filter(Boolean),
    [residenceName, comparedOrientation, comparedPosition],
  );

  const leftColumnWidth = 380;
  const scenarioCount = selectedScenarioIds.length + 1;
  const scenarioColumnWidth = 432;

  return (
    <Page>
      <Box
        color="brand.gray.120"
        cursor="pointer"
        display="inline-block"
        fontWeight="500"
        onClick={() =>
          navigate(`/residence/${residenceTypeId}/situation/${situationId}`)
        }
      >
        <ChakraImage
          display="inline-block"
          filter="var(--filter-gray-120)"
          src="/svg/chevron.svg"
          transform="rotate(-90deg) translateX(-2px)"
        />
        Terug naar scenario&apos;s
      </Box>

      <HStack alignItems="center" justifyContent="space-between" my={4}>
        <VStack alignItems="flex-start" gap={0}>
          <Flex alignItems="baseline" as="h1" fontWeight="bold" gap={4}>
            <Text fontSize={30}>Vergelijk:</Text>
            <Text color="brand.gray.120" fontSize={20}>
              {comparisonParameters.join(" | ")}
            </Text>
          </Flex>

          <Flex color="brand.gray.120" gap={8}>
            <FormControl alignItems="center" display="flex" gap={2} w="auto">
              <Switch
                id="only-changes"
                isChecked={onlyShowDifferences}
                onChange={(e) =>
                  setOnlyShowDifferences(e.currentTarget.checked)
                }
              />
              <FormLabel htmlFor="only-changes" mb="0" size="md">
                Toon alleen de veranderingen
              </FormLabel>
            </FormControl>

            <FormControl alignItems="center" display="flex" gap={2} w="auto">
              <Switch
                id="only-differences"
                isChecked={highlightDifferences}
                onChange={(e) =>
                  setHighlightDifferences(e.currentTarget.checked)
                }
              />
              <FormLabel htmlFor="only-differences" mb="0" size="md">
                Highlight verschillen
              </FormLabel>{" "}
              <InfoPopover imageProps={{ alignSelf: "baseline" }}>
                Verschillen tussen verduurzamingsmaatregelen worden gemarkeerd
              </InfoPopover>
            </FormControl>
          </Flex>
        </VStack>
        <Flex gap={4}>
          <ConfigureCompareViewModal />
          {recordingPdf ? (
            <Button
              disabled
              leftIcon={
                <CircularProgress
                  isIndeterminate
                  ringColor="currentcolor"
                  size="20px"
                />
              }
              ml="auto"
              variant="link"
            >
              Overzicht aan het genereren&hellip;
            </Button>
          ) : (
            <Button
              leftIcon={<DownloadIcon />}
              ml="auto"
              onClick={generateImage}
              variant="link"
            >
              Download overzicht (pdf)
            </Button>
          )}
        </Flex>
      </HStack>

      <Box id="table-wrapper" maxW={recordingPdf ? "unset" : "100svw"}>
        <Box
          minW="100%"
          w={
            leftColumnWidth +
            scenarioCount * scenarioColumnWidth +
            (recordingPdf ? 0 : scenarioColumnWidth)
          }
        >
          {!!currentSituation && (
            <ComparePageContextProvider
              value={{ situation: currentSituation, scenarios }}
            >
              <Box
                animation={draggingColumn ? reorderAnimation : "none"}
                id="tables"
                onDragEnter={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}
                pb={3}
              >
                {!!residenceType && (
                  <CompareBaseTable residenceType={residenceType} />
                )}
                <CompareGeneralTable />
                <CompareEnergyTable />
                <CompareEnergyMeasureTable />
                <CompareTotalCO2ImpactTable />
                <CompareTotalPriceTable />
              </Box>
            </ComparePageContextProvider>
          )}
        </Box>
      </Box>
    </Page>
  );
}
